import React, { lazy, Suspense } from 'react';
import './index.css';
import { Routes, Route } from 'react-router-dom';
import { useGoogleAnalytics } from './hooks/useGoogleAnalytics';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import AuthCallback from './components/auth/AuthCallback';
import Header from './components/layout/Header';
import LoadingSpinner from './components/common/LoadingSpinner';
import { LanguageProvider } from './contexts/LanguageContext';
import { LanguageRedirect } from './components/common/LanguageRedirect';
import { HelmetProvider } from 'react-helmet-async';
import { ThemeProvider } from './contexts/ThemeContext';
import RequestPasswordReset from './components/auth/RequestPasswordReset';
import UpdatePassword from './components/auth/UpdatePassword';
import FeedbackButton from './components/common/FeedbackButton';

// Lazy loaded components
const Home = lazy(() => import('./components/home/Home'));
const PDFToXMLConversion = lazy(() => import('./components/invoice/PDFToXMLConversion'));
const PDFToCIIConversion = lazy(() => import('./components/invoice/PDFToCIIConversion'));
const PDFToUBLConversion = lazy(() => import('./components/invoice/PDFToUBLConversion'));
const XMLToPDFConversion = lazy(() => import('./components/invoice/XMLToPDFConversion'));
const PDFToZUGFeRDConversion = lazy(() => import('./components/invoice/PDFToZUGFeRDConversion'));
const SimpleXRechnungViewer = lazy(() => import('./components/invoice/SimpleXRechnungViewer'));
const Imprint = lazy(() => import('./components/legal/Imprint'));
const SignUp = lazy(() => import('./components/auth/SignUp'));
const SignIn = lazy(() => import('./components/auth/SignIn'));
const Profile = lazy(() => import('./components/profile/Profile'));
const BlogPost = lazy(() => import('./components/blog/BlogPost'));
const BlogIndex = lazy(() => import('./components/blog/BlogIndex'));
const PrivacyPolicy = lazy(() => import('./components/legal/PrivacyPolicy'));
const TermsAndConditions = lazy(() => import('./components/legal/TermsAndConditions'));
const DPA = lazy(() => import('./components/legal/DPA'));
const ConvertPage = lazy(() => import('./components/common/ConvertPage'));
const Changelog = lazy(() => import('./components/changelog/Changelog'));
const MoneyBackGuarantee = lazy(() => import('./components/legal/MoneyBackGuarantee'));

const App: React.FC = () => {
  useGoogleAnalytics();

  return (
    <HelmetProvider>
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <div className="flex flex-col min-h-screen">
              <Header />
              <Suspense fallback={
                <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center">
                  <LoadingSpinner size="lg" color="var(--brand-primary)" />
                </div>
              }>
                <Routes>
                  {/* Root and invalid language redirects */}
                  <Route path="/" element={<LanguageRedirect />} />
                  <Route path="/*" element={<LanguageRedirect />} />

                  {/* Valid language routes */}
                  <Route path="/:lang">
                    <Route index element={<Home />} />
                    <Route path="sign-up" element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <SignUp />
                      </Suspense>
                    } />
                    <Route path="sign-in" element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <SignIn />
                      </Suspense>
                    } />
                    <Route path="auth">
                      <Route path="reset-password" element={<RequestPasswordReset />} />
                      <Route path="update-password" element={<UpdatePassword />} />
                      <Route path="callback" element={<AuthCallback />} />
                    </Route>
                    <Route path="convert" element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <ConvertPage />
                      </Suspense>
                    } />
                    <Route path="pdf-to-xrechnung" element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <PDFToXMLConversion />
                      </Suspense>
                    } />
                    <Route path="pdf-to-cii" element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <PDFToCIIConversion />
                      </Suspense>
                    } />
                    <Route path="pdf-to-ubl" element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <PDFToUBLConversion />
                      </Suspense>
                    } />
                    <Route path="xrechnung-to-pdf" element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <XMLToPDFConversion />
                      </Suspense>
                    } />
                    <Route path="pdf-to-zugferd" element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <PDFToZUGFeRDConversion />
                      </Suspense>
                    } />
                    <Route path="xrechnung-viewer" element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <SimpleXRechnungViewer />
                      </Suspense>
                    } />
                    <Route path="profile" element={
                      <ProtectedRoute>
                        <Suspense fallback={<LoadingSpinner />}>
                          <Profile />
                        </Suspense>
                      </ProtectedRoute>
                    } />
                    <Route path="imprint" element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <Imprint />
                      </Suspense>
                    } />
                    <Route path="privacy-policy" element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <PrivacyPolicy />
                      </Suspense>
                    } />
                    <Route path="dpa" element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <DPA />
                      </Suspense>
                    } />
                    <Route path="terms-and-conditions" element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <TermsAndConditions />
                      </Suspense>
                    } />
                    <Route path="blog" element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <BlogIndex />
                      </Suspense>
                    } />
                    <Route path="blog/:slug" element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <BlogPost />
                      </Suspense>
                    } />
                    <Route path="changelog" element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <Changelog />
                      </Suspense>
                    } />
                    <Route path="money-back-guarantee" element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <MoneyBackGuarantee />
                      </Suspense>
                    } />
                  </Route>
                </Routes>
                <FeedbackButton />
              </Suspense>
            </div>
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
};

export default App;